import { useEffect, useMemo, type FC } from 'react'
import { useForm, Controller } from 'react-hook-form'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useNavigate } from 'react-router'
import { useGetEnrollmentHYCSDisclosuresData, useSubmitEnrollmentHYCSDisclosuresData } from '@egw/api'
import { Button, Alert, AlertType, GenericError } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { Logger } from '@egw/logger'
import { EnrollmentHYCSLayout } from '@egw/layouts'
import { sendEvent } from '@egw/events'
import { configClient } from '@egw/config'
import { WEALTH_ROUTES } from '@egw/constants'
import { DisclosureSection, PageHeader } from '../../../components'
import { useValidateRoute } from '../../../hooks/navigation/validate-route'
import { EDataLayerEventWealth } from '../../../constants/events/EDataLayerEventWealth'
import { type Config } from '../../../config/config'
import { getHYCSDisclosures } from '../../../utils/disclosures/get-hycs-disclosures'
import { getArticlePath } from '../../../utils/disclosures/get-disclosures-path'
import { newPathIsDifferent } from '../../../utils/general/new-path-is-different'
import { ROUTES } from '../../../constants/routes/routes-generic'

const log = new Logger('Enrollment - HYCS - Disclosures')

interface FormData {
  email: string
}

export const DisclosuresPage: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const navigate = useNavigate()
  const config = configClient.getConfig() as Config

  useEffect(() => {
    sendEvent(EDataLayerEventWealth.USER_DISCLOSURES_STARTED)
  }, [])

  const disclosures = useMemo(() => {
    return getHYCSDisclosures(config.zendeskArticles)
  }, [config.zendeskArticles])

  const { data, isLoading, error } = useGetEnrollmentHYCSDisclosuresData()
  const { mutate, isLoading: isSubmitting, error: submitError } = useSubmitEnrollmentHYCSDisclosuresData()

  const {
    control,
    handleSubmit,
    formState: { errors: formErrors, isValid },
    watch,
  } = useForm({
    defaultValues: {
      egwa: false,
      csa: false,
    },
  })

  const isCheckedEGWA = watch('egwa')
  const isCheckedCSA = watch('csa')

  // Dispatch events when disclosure section is checked
  useEffect(() => {
    if (isCheckedEGWA) {
      sendEvent(EDataLayerEventWealth.BANKHYCHYS_WEALTHADVISORS_CONSENT)
    }
    if (isCheckedCSA) {
      sendEvent(EDataLayerEventWealth.BANKHYCHYS_CHECKINGSAVINGS_CONSENT)
    }
  }, [isCheckedEGWA, isCheckedCSA])

  // Protect route
  useValidateRoute(data?.path)

  const onSubmit = handleSubmit(async (fData: unknown): Promise<void> => {
    const formData = fData as FormData

    if (isValid) {
      const disclosureData = {
        // CSA
        depositAgreementDisclosure: {
          accepted: true,
          id: getArticlePath(config.zendeskArticles.depositAgreement),
        },
        taxCertificationForUSPersonsDisclosure: {
          accepted: true,
          id: getArticlePath(config.zendeskArticles.taxCertification),
        },
        privacyNotice: {
          accepted: true,
          id: getArticlePath(config.zendeskArticles.privacyPolicySection),
        },
        // EGWA
        investmentAdvisoryAgreementDisclosure: {
          accepted: true,
          id: getArticlePath(config.zendeskArticles.investmentAdvisoryAgreement),
        },
        advWrapFeeDisclosure: {
          accepted: true,
          id: getArticlePath(config.zendeskArticles.advPart2A2b),
        },
        customerRelationshipSummaryDisclosure: {
          accepted: true,
          id: getArticlePath(config.zendeskArticles.customerRelationshipAgreement),
        },
        advisoryPrivacyNotice: {
          accepted: true,
          id: getArticlePath(config.zendeskArticles.privacyPolicyAdvisors),
        },
        triggerRiskCheck: true,
      }

      sendEvent(EDataLayerEventWealth.BANKHYCHYS_CREATEACCOUNT_SUBMIT)
      sendEvent(EDataLayerEventWealth.MILESTONE_DISCLOSURES_CONSENT, {
        disclosures: disclosureData,
      })
      sendEvent(EDataLayerEventWealth.MILESTONE_CREATE_ACCOUNT_CLICKED)

      try {
        const response = await mutate(disclosureData)
        // If path exists and is different
        if (response?.path && newPathIsDifferent(location.pathname, response.path)) {
          sendEvent(EDataLayerEventWealth.EGM_SUBMISSION_DISCLOSURES_ACCEPTED)
          sendEvent(EDataLayerEventWealth.MILESTONE_ACCOUNT_CREATED)
          sendEvent(EDataLayerEventWealth.BANKHYCHYS_ACCOUNT_CREATED)
          navigate(response.path)
        } else {
          // If no path from BE, redirect to error
          if (!response?.path) {
            navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${ROUTES.ERROR.path}`)
          }
        }
      } catch (e) {
        log.error('Error submitting form', e)
      }
    }
  })

  return (
    <EnrollmentHYCSLayout title={t('hycs.disclosures.page_title')}>
      <div className="egw-container-enroll-hycs h-full flex flex-col relative md:justify-center justify-start items-start">
        {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
        {isLoading ? (
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        ) : null}

        {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
        {!isLoading && !error ? (
          <form
            onSubmit={(e) => void onSubmit(e)}
            className="w-full flex-1 flex flex-col justify-start lg:justify-center"
          >
            {/* Alert */}
            <Alert
              alertType={AlertType.Success}
              description={t('common.email_verified')}
              showIcon
              className="mb-md"
              testId="success-alert"
            />
            <PageHeader title={t('hycs.disclosures.title')} />
            <p className="typo-primary-base text-text-meta mb-md">{t('hycs.disclosures.description')}</p>

            {disclosures.map((e, i) => {
              return (
                <Controller
                  name={e.name as 'egwa' | 'csa'}
                  control={control}
                  rules={{
                    required: t('common.required_field'),
                  }}
                  render={({ field }) => (
                    <DisclosureSection
                      checkboxHelperText={e.helper}
                      checkboxLabel={e.title}
                      expandedLinks={e.links}
                      expandedText={e.expanded}
                      hasBorder={disclosures.length !== i + 1}
                      testId={`disclosure-${e.title.toLowerCase().replaceAll(' ', '-')}`}
                      onCheckboxChange={field.onChange}
                      alwaysExpanded
                    />
                  )}
                  key={`disclosure-${e.title.replaceAll(' ', '-')}`}
                />
              )
            })}

            <div className="flex-1 md:hidden" />

            {submitError ? (
              <Alert alertType={AlertType.Error} description={t('common.generic_error')} className="mt-sm" />
            ) : null}

            {/* AUTO GENERATED COMPONENTS */}

            <Button
              type="submit"
              className="w-full md:w-min mt-xl"
              isDisabled={!isValid}
              isLoading={isSubmitting}
              testId="primaryAction_button"
            >
              {t('common.continue')}
            </Button>
          </form>
        ) : null}

        {/* This error handles the case when the initial data is not loaded, for example network error */}
        {error ? (
          <div className="flex-1 flex flex-col justify-start md:justify-center">
            <GenericError errorMessage={t('common.generic_error')} />
          </div>
        ) : null}
      </div>
    </EnrollmentHYCSLayout>
  )
}
